import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, Mail, Phone } from "lucide-react";

export default function AllShows() {
  const [shows, setShows] = useState([]);
  const [users, setUsers] = useState([]);
  const { impersonate } = useAuth();
  const navigate = useNavigate();

  const viewAs = async (u) => {
    await impersonate(u.id);
    navigate("/dashboard");
  };

  const load = useCallback(async () => {
    const [s, u] = await Promise.all([api.get("/admin/shows"), api.get("/admin/users")]);
    setShows(s.data); setUsers(u.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleSuspend = async (user) => {
    if (user.role === "super_admin") return;
    await api.put(`/admin/users/${user.id}`, { suspended: !user.suspended });
    load();
  };

  const setTier = async (user, tier) => {
    await api.put(`/admin/users/${user.id}`, { access_tier: tier });
    load();
  };

  const userFor = (s) => users.find(u => u.id === s.owner_user_id);

  return (
    <div className="pgm-reveal">
      <div className="pgm-micro">◆ Super Admin</div>
      <h1 className="pgm-display" style={{ fontSize: 44 }}>All Shows</h1>
      <p style={{ color: "var(--pgm-wine)", opacity: 0.75, marginBottom: 22 }}>Every show on the platform. Suspend, reinstate, or change access tier.</p>

      <table className="pgm-table" data-testid="all-shows-table">
        <thead><tr><th></th><th>Show</th><th>Owner</th><th>Contact</th><th>Tier</th><th>Guests</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {shows.map(s => {
            const u = userFor(s);
            return (
              <tr key={s.id} data-testid={`show-row-${s.id}`}>
                <td data-label="Logo">
                  {s.podcast_logo_url ? (
                    <img src={s.podcast_logo_url.startsWith("/api") ? `${BACKEND_URL}${s.podcast_logo_url}` : s.podcast_logo_url} alt="" style={{ width: 36, height: 36, objectFit: "cover", border: "1px solid var(--pgm-rule)" }} />
                  ) : (
                    <div style={{ width: 36, height: 36, background: "var(--pgm-amber)", border: "1px solid var(--pgm-wine)" }} />
                  )}
                </td>
                <td data-label="Show"><strong>{s.show_name}</strong></td>
                <td data-label="Owner" style={{ fontSize: 12 }}>{u ? <>{u.name}<br/><span style={{ opacity: 0.65 }}>{u.email}</span></> : "—"}</td>
                <td data-label="Contact" style={{ fontSize: 12 }} data-testid={`contact-${s.id}`}>
                  {u && (u.contact_email || u.contact_phone) ? (
                    <>
                      {u.contact_email && (
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <Mail size={11} color="var(--pgm-magenta)" />
                          <a className="pgm-link" href={`mailto:${u.contact_email}`}>{u.contact_email}</a>
                        </div>
                      )}
                      {u.contact_phone && (
                        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3 }}>
                          <Phone size={11} color="var(--pgm-magenta)" />
                          <a className="pgm-link" href={`tel:${u.contact_phone}`}>{u.contact_phone}</a>
                        </div>
                      )}
                    </>
                  ) : (
                    <span style={{ opacity: 0.5 }}>Not provided</span>
                  )}
                </td>
                <td data-label="Tier">
                  <select className="pgm-select" style={{ width: 110 }} value={u?.access_tier || s.access_tier || "free"} onChange={(e) => u && setTier(u, e.target.value)} data-testid={`tier-${s.id}`}>
                    <option value="free">Free</option><option value="paid">Paid</option>
                  </select>
                </td>
                <td data-label="Guests">{s.guest_count}</td>
                <td data-label="Status">
                  {u?.suspended ? <span className="pgm-badge pgm-badge-orange">Suspended</span> : <span className="pgm-badge pgm-badge-olive">Active</span>}
                </td>
                <td data-label="Actions" style={{ whiteSpace: "nowrap" }}>
                  {u && u.role !== "super_admin" && !u.suspended && (
                    <button className="pgm-btn pgm-btn-olive" style={{ padding: "5px 12px", fontSize: 10, marginRight: 4 }} onClick={() => viewAs(u)} data-testid={`view-as-${s.id}`}>
                      <Eye size={11} /> View as
                    </button>
                  )}
                  {u && u.role !== "super_admin" && (
                    <button className="pgm-btn pgm-btn-ghost" style={{ padding: "5px 12px", fontSize: 10 }} onClick={() => toggleSuspend(u)} data-testid={`toggle-${s.id}`}>
                      {u.suspended ? "Reinstate" : "Suspend"}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
